import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useBookingFailures, type BookingFailureRow } from "../api/queries/reconciliation";
import { useTerminalDropdown } from "../components/ui/useTerminalDropdown";

const MAX_PREVIEW = 5;

const ERROR_KIND_SHORT: Record<string, string> = {
  InstrumentNotRegistered: "Instr. not found",
  BookingPersistError: "DB error",
  IssuerNotFound: "Issuer missing",
  PositionDatabaseUnavailable: "DB unavailable",
  MissingInteriorParty: "Party missing",
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function FailureItem({
  row,
  onClick,
}: {
  row: BookingFailureRow;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full flex-col gap-0.5 border-b border-border px-3 py-2 text-left hover:bg-[var(--bg-secondary,#1a1a1a)] last:border-b-0"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[9px] font-semibold text-[var(--fg-primary,#e5e5e5)] truncate max-w-[160px]">
          {row.instrument_id ?? row.venue_trade_id}
        </span>
        <span className="shrink-0 text-[9px] text-muted">{timeAgo(row.failed_at)} ago</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="rounded-none bg-zinc-800 px-1 py-0.5 font-mono text-[8px] text-zinc-300">
          {row.venue}
        </span>
        <span className="truncate text-[9px] text-orange-300">
          {ERROR_KIND_SHORT[row.error_kind] ?? row.error_kind}
        </span>
        {row.replay_attempts > 0 && (
          <span className="ml-auto shrink-0 text-[8px] text-muted">{row.replay_attempts}× replayed</span>
        )}
      </div>
    </button>
  );
}

export function AlertsMenu() {
  const { data: rawData } = useBookingFailures({ refetchInterval: 30_000 });
  const failures: BookingFailureRow[] = Array.isArray(rawData) ? rawData : [];
  const count = failures.length;

  const navigate = useNavigate();
  const { open, toggle, close, triggerRef, panelRef, pos } = useTerminalDropdown("right");

  const goToMO = (issueId?: string) => {
    close();
    navigate(
      issueId
        ? `/middle-office?focus=mo-booking-issues&issue=${issueId}`
        : "/middle-office?focus=mo-booking-issues",
    );
  };

  const preview = failures.slice(0, MAX_PREVIEW);
  const hasMore = count > MAX_PREVIEW;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        title={count > 0 ? `${count} unacknowledged booking issue${count > 1 ? "s" : ""}` : "No booking issues"}
        className="relative flex items-center justify-center rounded-none px-1.5 py-0.5 text-[13px] text-muted hover:text-[var(--fg-secondary,#d0d0d0)]"
        aria-haspopup="true"
        aria-expanded={open}
      >
        🔔
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-red-600 px-0.5 font-mono text-[7px] font-bold leading-none text-white">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            style={{ top: pos.top, right: window.innerWidth - (pos.left), left: "unset" }}
            className="fixed z-[9999] w-72 rounded-none border border-border bg-panel shadow-lg"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-3 py-2">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--fg-primary,#e5e5e5)]">
                System Alerts
              </span>
              {count > 0 && (
                <span className="rounded-none bg-red-900/50 px-1.5 py-0.5 text-[8px] font-semibold text-red-300">
                  {count} booking issue{count > 1 ? "s" : ""}
                </span>
              )}
            </div>

            {/* Body */}
            {count === 0 ? (
              <div className="px-3 py-4 text-center text-[10px] text-muted">
                <div className="mb-1 text-base">✓</div>
                All trades booked successfully
              </div>
            ) : (
              <>
                <div className="border-b border-border bg-red-950/20 px-3 py-1.5">
                  <p className="text-[9px] text-red-200">
                    {count} trade{count > 1 ? "s" : ""} failed to book and{" "}
                    {count > 1 ? "require" : "requires"} attention.
                    Positions may be incorrect until resolved.
                  </p>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {preview.map((row) => (
                    <FailureItem
                      key={row.id}
                      row={row}
                      onClick={() => goToMO(row.id)}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Footer */}
            <div className="border-t border-border px-3 py-2">
              <button
                type="button"
                onClick={() => goToMO()}
                className="w-full rounded-none border border-border bg-bg py-1 text-[9px] text-[var(--color-info,#0ea5e9)] hover:bg-[var(--bg-secondary,#1a1a1a)]"
              >
                {count > 0
                  ? `View all ${count} issue${count > 1 ? "s" : ""} in Middle Office →`
                  : "Open Middle Office →"}
              </button>
              {hasMore && (
                <p className="mt-1 text-center text-[8px] text-muted">
                  Showing {MAX_PREVIEW} of {count}
                </p>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
